import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { TbAddressBook, TbCheck, TbMail, TbPhone, TbUsers } from "react-icons/tb";
import type { Passenger } from "src/Modules/Flights/constants/JourneyResult";
import { COUNTRIES_CODES, NATIONALITIES } from "src/Seed/Countries/Countries";

const EMPTY_VALUE = "-";

const formatDate = (date?: string | null) => (date ? dayjs(date).format("YYYY-MM-DD") : EMPTY_VALUE);

const getCountryName = (code?: string | null) => (code ? (COUNTRIES_CODES.find((country) => country.value === code)?.label ?? code) : EMPTY_VALUE);

// The booking payload sends nationality as `{ countryCode }`, falling back to the country sent alongside it
const getNationality = (passenger: Passenger) => {
  const nationality = passenger.nationality as string | { countryCode?: string | null } | null;
  const code = (typeof nationality === "string" ? nationality : nationality?.countryCode) ?? passenger.country?.countryCode;
  return code ? (NATIONALITIES.find((country) => country.value === code)?.label ?? code) : EMPTY_VALUE;
};

const capitalize = (value?: string | null) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : EMPTY_VALUE);

const getInitials = (passenger: Passenger) => `${passenger.firstName?.[0] ?? ""}${passenger.lastName?.[0] ?? ""}`;

// Child / Infant titles are type codes ("CHD", "Inf"), not salutations
const PASSENGER_TYPE_TITLES = ["CHD", "INF"];

const getFullName = (passenger: Passenger) => {
  const title = PASSENGER_TYPE_TITLES.includes(passenger.title?.toUpperCase()) ? null : passenger.title;
  return [title, passenger.firstName, passenger.lastName].filter(Boolean).join(" ");
};

// Infants don't provide travel documents during booking, so only Adult / Child have document fields
const hasTravelDocuments = (passenger: Passenger) => passenger.type !== "Infant";

const PassengerField: FC<{ title: ReactNode; value: ReactNode }> = ({ title, value }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <div className="text-font-subtle text-xs uppercase">{title}</div>
    <div className="text-font-default truncate text-sm">{value}</div>
  </div>
);

const PassengerCard: FC<{ passenger: Passenger; index: number }> = ({ passenger, index }) => {
  const { t } = useTranslation();
  const idDetail = passenger.idDetails?.[0];

  const showDocuments = hasTravelDocuments(passenger);

  const fields = [
    { key: "type", value: t(`Flights.details.passengerType.${passenger.type?.toLowerCase()}`, { defaultValue: passenger.type }) },
    showDocuments && { key: "nationality", value: getNationality(passenger) },
    { key: "dateOfBirth", value: formatDate(passenger.dateOfBirth) },
    { key: "gender", value: capitalize(passenger.gender) },
    showDocuments && { key: "countryOfIssue", value: getCountryName(passenger.passengerIdIssueCountryCode ?? idDetail?.issuedCountryCode) },
    showDocuments && { key: "issueDate", value: formatDate(passenger.passportIssueDate ?? passenger.passengerIdIssueDate ?? idDetail?.issueDate) },
    showDocuments && { key: "passportNumber", value: passenger.passportNo || passenger.passengerIdNo || idDetail?.idNumber || EMPTY_VALUE },
    showDocuments && { key: "expiryDate", value: formatDate(passenger.passportExpiry ?? passenger.passengerIdExpiry ?? idDetail?.expiryDate) },
  ].filter((field): field is { key: string; value: string } => !!field);

  return (
    <div className="border-border-subtle overflow-hidden rounded-md border">
      <div className="bg-background-default border-border-subtle flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-b px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="bg-background-primary-lighter text-font-active flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold uppercase">
            {getInitials(passenger)}
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="text-font-subtle text-xs uppercase">{t("BookingResult.passengerDetails.passengerNumber", { number: index + 1 })}</span>
            <span className="text-font-default text-sm font-semibold break-words">
              {getFullName(passenger)}
            </span>
          </div>
        </div>
        {passenger.isLeadPax && (
          <span className="bg-background-primary text-font-inverted rounded-pill flex shrink-0 items-center gap-1 px-3 py-1 text-xs font-medium">
            <TbCheck className="size-3.5" />
            {t("BookingResult.passengerDetails.primaryContact")}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4 p-4 md:grid-cols-4">
        {fields.map((field) => (
          <PassengerField key={field.key} title={t(`BookingResult.passengerDetails.fields.${field.key}`)} value={field.value} />
        ))}
      </div>
    </div>
  );
};

const ContactInformation: FC<{ passenger: Passenger }> = ({ passenger }) => {
  const { t } = useTranslation();
  const mobile = [passenger.mobile1CountryCode, passenger.mobile1].filter(Boolean).join(" ");

  return (
    <div className="border-border-subtle bg-background-minimal flex flex-col gap-4 rounded-md border p-4">
      <div className="flex items-center gap-3">
        <TbAddressBook className="text-font-subtle size-5 shrink-0" />
        <div className="flex flex-col">
          <span className="text-font-subtle text-xs uppercase">{t("BookingResult.passengerDetails.contactInformation")}</span>
          <span className="text-font-default text-sm font-semibold">{t("BookingResult.passengerDetails.primaryContactDetails")}</span>
        </div>
      </div>
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-font-subtle text-xs font-semibold uppercase">{t("BookingResult.passengerDetails.primaryContactEmail")}</span>
          <div className="text-font-default flex min-w-0 items-center gap-2 text-base">
            <TbMail className="size-5 shrink-0" />
            <span className="truncate">{passenger.email || EMPTY_VALUE}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1 md:items-end">
          <span className="text-font-subtle text-xs font-semibold uppercase">{t("BookingResult.passengerDetails.mobilePhone")}</span>
          <div className="text-font-default flex items-center gap-2 text-base" dir="ltr">
            <TbPhone className="size-5 shrink-0" />
            <span>{mobile || EMPTY_VALUE}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PassengerDetails: FC<{ passengers?: Passenger[] }> = ({ passengers = [] }) => {
  const { t } = useTranslation();
  const leadPassenger = passengers.find((passenger) => passenger.isLeadPax) ?? passengers[0];
  const primaryCount = passengers.filter((passenger) => passenger.isLeadPax).length;

  return (
    <div className="bg-background-default border-border-subtle flex w-full flex-col overflow-hidden rounded-md border shadow-xs">
      <div className="bg-background-default border-border-subtle flex items-center justify-between gap-2 border-b px-6 py-4">
        <div className="flex items-center gap-2">
          <TbUsers className="text-font-active size-5" />
          <div className="text-font-default text-base font-semibold">{t("BookingResult.passengerDetails.sectionTitle")}</div>
        </div>
        <div className="text-font-subtle text-xs">
          {t("BookingResult.passengerDetails.passengersCount", { count: passengers.length })} •{" "}
          {t("BookingResult.passengerDetails.primaryCount", { count: primaryCount })}
        </div>
      </div>
      <div className="flex flex-col gap-3 p-6">
        {passengers.map((passenger, index) => (
          <PassengerCard key={passenger.paxId ?? index} passenger={passenger} index={index} />
        ))}
        {leadPassenger && <ContactInformation passenger={leadPassenger} />}
      </div>
    </div>
  );
};

export default PassengerDetails;
